/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useReveal } from '../hooks/useReveal';
import SEO from '../../components/SEO';
import {
  SHELL,
  SECTION,
  H1,
  H2,
  LEAD,
  LEAD_DARK,
  BTN_PRIMARY,
  BTN_ON_LIGHT,
  EYEBROW_DARK,
  RULE,
} from '../theme';

/**
 * Libelles de la page, traduits. Ils etaient ecrits en francais dans le JSX :
 * un visiteur anglophone ou arabophone lisait une page a moitie francaise.
 */
const COPY = {
  fr: {
    back: 'Retour aux services',
    benefits: 'Bénéfices',
    ctaTitle: 'Prêt à démarrer votre projet ?',
    ctaLead: 'Parlons de vos besoins et de la manière dont nous pouvons vous aider.',
    cta: 'Contactez-nous',
    missingTitle: 'Service introuvable',
    missingLead: "Cette page n'existe pas ou plus. Retrouvez l'ensemble de nos expertises.",
  },
  en: {
    back: 'Back to services',
    benefits: 'Benefits',
    ctaTitle: 'Ready to start your project?',
    ctaLead: 'Let us discuss your needs and how we can help.',
    cta: 'Contact us',
    missingTitle: 'Service not found',
    missingLead: 'This page no longer exists. Browse all our areas of expertise.',
  },
  ar: {
    back: 'العودة إلى الخدمات',
    benefits: 'الفوائد',
    ctaTitle: 'هل أنتم مستعدون لبدء مشروعكم؟',
    ctaLead: 'لنتحدث عن احتياجاتكم وكيف يمكننا المساعدة.',
    cta: 'اتصل بنا',
    missingTitle: 'الخدمة غير موجودة',
    missingLead: 'هذه الصفحة لم تعد موجودة. تصفح جميع خبراتنا.',
  },
};

export const ServiceDetail = () => {
  const { slug } = useParams();
  const { t, language } = useLanguage();

  const serviceDetails = {
    'digital-transformation': {
      key: 'digitalTransformation',
      details: {
        fr: {
          overview: 'La transformation digitale est bien plus qu\'une simple modernisation technologique. C\'est une révolution stratégique qui repense entièrement les processus métier, la culture d\'entreprise et l\'expérience client.',
          approach: 'Notre Approche',
          steps: [
            { title: 'Audit & Diagnostic', desc: 'Évaluation complète de votre maturité digitale' },
            { title: 'Stratégie Digitale', desc: 'Élaboration d\'une feuille de route sur mesure' },
            { title: 'Mise en œuvre', desc: 'Déploiement progressif des solutions' },
            { title: 'Accompagnement', desc: 'Formation et support continu des équipes' }
          ],
          benefits: [
            'Augmentation de la productivité de 40%',
            'Réduction des coûts opérationnels',
            'Amélioration de l\'expérience client',
            'Agilité et innovation accrue'
          ]
        },
        en: {
          overview: 'Digital transformation is much more than simple technological modernization. It\'s a strategic revolution that completely rethinks business processes, corporate culture and customer experience.',
          approach: 'Our Approach',
          steps: [
            { title: 'Audit & Diagnosis', desc: 'Complete assessment of your digital maturity' },
            { title: 'Digital Strategy', desc: 'Development of a tailored roadmap' },
            { title: 'Implementation', desc: 'Progressive deployment of solutions' },
            { title: 'Support', desc: 'Continuous training and support for teams' }
          ],
          benefits: [
            '40% increase in productivity',
            'Reduction in operational costs',
            'Improved customer experience',
            'Increased agility and innovation'
          ]
        },
        ar: {
          overview: 'التحول الرقمي هو أكثر بكثير من مجرد تحديث تكنولوجي. إنها ثورة استراتيجية تعيد التفكير بالكامل في العمليات التجارية وثقافة الشركة وتجربة العملاء.',
          approach: 'نهجنا',
          steps: [
            { title: 'المراجعة والتشخيص', desc: 'تقييم كامل لنضجك الرقمي' },
            { title: 'الاستراتيجية الرقمية', desc: 'تطوير خارطة طريق مخصصة' },
            { title: 'التنفيذ', desc: 'النشر التدريجي للحلول' },
            { title: 'الدعم', desc: 'التدريب والدعم المستمر للفرق' }
          ],
          benefits: [
            'زيادة الإنتاجية بنسبة 40٪',
            'تقليل التكاليف التشغيلية',
            'تحسين تجربة العملاء',
            'زيادة المرونة والابتكار'
          ]
        }
      }
    },
    'consulting': {
      key: 'consulting',
      details: {
        fr: {
          overview: 'Notre expertise en conseil IT vous permet d\'optimiser votre infrastructure technologique, de réduire les coûts et d\'améliorer les performances opérationnelles.',
          approach: 'Notre Méthodologie',
          steps: [
            { title: 'Analyse', desc: 'Audit complet de votre infrastructure IT' },
            { title: 'Recommandations', desc: 'Plan d\'action stratégique détaillé' },
            { title: 'Optimisation', desc: 'Mise en place des améliorations' },
            { title: 'Suivi', desc: 'Monitoring continu des performances' }
          ],
          benefits: [
            'Optimisation des coûts IT jusqu\'à 40%',
            'Architecture scalable et sécurisée',
            'Amélioration de la disponibilité',
            'Conformité réglementaire assurée'
          ]
        },
        en: {
          overview: 'Our IT consulting expertise allows you to optimize your technology infrastructure, reduce costs and improve operational performance.',
          approach: 'Our Methodology',
          steps: [
            { title: 'Analysis', desc: 'Complete audit of your IT infrastructure' },
            { title: 'Recommendations', desc: 'Detailed strategic action plan' },
            { title: 'Optimization', desc: 'Implementation of improvements' },
            { title: 'Monitoring', desc: 'Continuous performance monitoring' }
          ],
          benefits: [
            'IT cost optimization up to 40%',
            'Scalable and secure architecture',
            'Improved availability',
            'Regulatory compliance ensured'
          ]
        },
        ar: {
          overview: 'تتيح لك خبرتنا في الاستشارات التقنية تحسين البنية التحتية التكنولوجية الخاصة بك وتقليل التكاليف وتحسين الأداء التشغيلي.',
          approach: 'منهجيتنا',
          steps: [
            { title: 'التحليل', desc: 'مراجعة كاملة للبنية التحتية لتكنولوجيا المعلومات' },
            { title: 'التوصيات', desc: 'خطة عمل استراتيجية مفصلة' },
            { title: 'التحسين', desc: 'تنفيذ التحسينات' },
            { title: 'المتابعة', desc: 'مراقبة الأداء المستمرة' }
          ],
          benefits: [
            'تحسين تكلفة تكنولوجيا المعلومات بنسبة تصل إلى 40٪',
            'بنية قابلة للتطوير وآمنة',
            'تحسين التوافر',
            'ضمان الامتثال التنظيمي'
          ]
        }
      }
    },
    'systems-integration': {
      key: 'integration',
      details: {
        fr: {
          overview: 'L\'intégration de systèmes permet de connecter vos applications, bases de données et services pour créer un écosystème IT cohérent et performant.',
          approach: 'Notre Expertise',
          steps: [
            { title: 'Cartographie', desc: 'Analyse de l\'existant et des besoins' },
            { title: 'Architecture', desc: 'Conception de la solution d\'intégration' },
            { title: 'Développement', desc: 'Mise en place des connecteurs' },
            { title: 'Tests & Validation', desc: 'Vérification de l\'intégration' }
          ],
          benefits: [
            'Élimination des silos de données',
            'Automatisation des processus',
            'Réduction des erreurs manuelles',
            'Temps de traitement divisé par 10'
          ]
        },
        en: {
          overview: 'Systems integration connects your applications, databases and services to create a coherent and efficient IT ecosystem.',
          approach: 'Our Expertise',
          steps: [
            { title: 'Mapping', desc: 'Analysis of existing systems and needs' },
            { title: 'Architecture', desc: 'Integration solution design' },
            { title: 'Development', desc: 'Connector implementation' },
            { title: 'Testing & Validation', desc: 'Integration verification' }
          ],
          benefits: [
            'Elimination of data silos',
            'Process automation',
            'Reduction of manual errors',
            'Processing time reduced by 10x'
          ]
        },
        ar: {
          overview: 'يربط تكامل الأنظمة تطبيقاتك وقواعد البيانات والخدمات لإنشاء نظام بيئي متماسك وفعال لتكنولوجيا المعلومات.',
          approach: 'خبرتنا',
          steps: [
            { title: 'التخطيط', desc: 'تحليل الأنظمة الحالية والاحتياجات' },
            { title: 'الهندسة المعمارية', desc: 'تصميم حل التكامل' },
            { title: 'التطوير', desc: 'تنفيذ الموصلات' },
            { title: 'الاختبار والتحقق', desc: 'التحقق من التكامل' }
          ],
          benefits: [
            'القضاء على صوامع البيانات',
            'أتمتة العمليات',
            'تقليل الأخطاء اليدوية',
            'تقليل وقت المعالجة بمقدار 10 مرات'
          ]
        }
      }
    },
    'development': {
      key: 'development',
      details: {
        fr: {
          overview: 'Nous développons des applications web et desktop performantes, évolutives et adaptées à vos besoins métier spécifiques.',
          approach: 'Technologies',
          steps: [
            { title: 'Analyse', desc: 'Compréhension approfondie des besoins' },
            { title: 'Design', desc: 'Conception UX/UI moderne' },
            { title: 'Développement', desc: 'Code de qualité professionnelle' },
            { title: 'Déploiement', desc: 'Mise en production sécurisée' }
          ],
          benefits: [
            'React, Angular, Vue.js pour le frontend',
            'FastAPI, Node.js, .NET pour le backend',
            'Applications desktop multiplateformes',
            'Support et maintenance continue'
          ]
        },
        en: {
          overview: 'We develop high-performance, scalable web and desktop applications tailored to your specific business needs.',
          approach: 'Technologies',
          steps: [
            { title: 'Analysis', desc: 'Deep understanding of needs' },
            { title: 'Design', desc: 'Modern UX/UI design' },
            { title: 'Development', desc: 'Professional quality code' },
            { title: 'Deployment', desc: 'Secure production deployment' }
          ],
          benefits: [
            'React, Angular, Vue.js for frontend',
            'FastAPI, Node.js, .NET for backend',
            'Cross-platform desktop applications',
            'Continuous support and maintenance'
          ]
        },
        ar: {
          overview: 'نقوم بتطوير تطبيقات ويب وسطح مكتب عالية الأداء وقابلة للتطوير ومصممة خصيصًا لاحتياجات عملك المحددة.',
          approach: 'التقنيات',
          steps: [
            { title: 'التحليل', desc: 'فهم عميق للاحتياجات' },
            { title: 'التصميم', desc: 'تصميم UX/UI حديث' },
            { title: 'التطوير', desc: 'كود بجودة احترافية' },
            { title: 'النشر', desc: 'نشر إنتاج آمن' }
          ],
          benefits: [
            'React و Angular و Vue.js للواجهة الأمامية',
            'FastAPI و Node.js و .NET للخلفية',
            'تطبيقات سطح المكتب متعددة المنصات',
            'الدعم والصيانة المستمرة'
          ]
        }
      }
    },
    'devops': {
      key: 'devops',
      details: {
        fr: {
          overview: 'Automatisez vos déploiements, améliorez la collaboration et accélérez votre time-to-market avec nos solutions DevOps.',
          approach: 'Nos Solutions',
          steps: [
            { title: 'CI/CD', desc: 'Pipelines d\'intégration et déploiement continus' },
            { title: 'Containerisation', desc: 'Docker et Kubernetes' },
            { title: 'IaC', desc: 'Infrastructure as Code avec Terraform' },
            { title: 'Monitoring', desc: 'Surveillance et alertes en temps réel' }
          ],
          benefits: [
            'Déploiements 80% plus rapides',
            'Réduction des erreurs de production',
            'Scalabilité automatique',
            'Coûts d\'infrastructure optimisés'
          ]
        },
        en: {
          overview: 'Automate your deployments, improve collaboration and accelerate your time-to-market with our DevOps solutions.',
          approach: 'Our Solutions',
          steps: [
            { title: 'CI/CD', desc: 'Continuous integration and deployment pipelines' },
            { title: 'Containerization', desc: 'Docker and Kubernetes' },
            { title: 'IaC', desc: 'Infrastructure as Code with Terraform' },
            { title: 'Monitoring', desc: 'Real-time monitoring and alerts' }
          ],
          benefits: [
            '80% faster deployments',
            'Reduced production errors',
            'Automatic scalability',
            'Optimized infrastructure costs'
          ]
        },
        ar: {
          overview: 'أتمتة عمليات النشر وتحسين التعاون وتسريع وقت الوصول إلى السوق باستخدام حلول DevOps الخاصة بنا.',
          approach: 'حلولنا',
          steps: [
            { title: 'CI/CD', desc: 'خطوط التكامل والنشر المستمر' },
            { title: 'الحاويات', desc: 'Docker و Kubernetes' },
            { title: 'IaC', desc: 'البنية التحتية ككود مع Terraform' },
            { title: 'المراقبة', desc: 'المراقبة والتنبيهات في الوقت الفعلي' }
          ],
          benefits: [
            'عمليات نشر أسرع بنسبة 80٪',
            'تقليل أخطاء الإنتاج',
            'قابلية التوسع التلقائية',
            'تكاليف البنية التحتية المحسنة'
          ]
        }
      }
    },
    'networking': {
      key: 'networking',
      details: {
        fr: {
          overview: 'Concevez et déployez une infrastructure réseau robuste, sécurisée et performante pour supporter la croissance de votre entreprise.',
          approach: 'Nos Services',
          steps: [
            { title: 'Architecture', desc: 'Conception de l\'infrastructure réseau' },
            { title: 'Sécurité', desc: 'Firewalls, VPN, et protection DDoS' },
            { title: 'Déploiement', desc: 'Installation et configuration' },
            { title: 'Maintenance', desc: 'Support 24/7 et monitoring' }
          ],
          benefits: [
            'Disponibilité 99.99%',
            'Sécurité multi-couches',
            'SD-WAN pour sites distants',
            'Optimisation de la bande passante'
          ]
        },
        en: {
          overview: 'Design and deploy a robust, secure and high-performance network infrastructure to support your business growth.',
          approach: 'Our Services',
          steps: [
            { title: 'Architecture', desc: 'Network infrastructure design' },
            { title: 'Security', desc: 'Firewalls, VPN, and DDoS protection' },
            { title: 'Deployment', desc: 'Installation and configuration' },
            { title: 'Maintenance', desc: '24/7 support and monitoring' }
          ],
          benefits: [
            '99.99% availability',
            'Multi-layer security',
            'SD-WAN for remote sites',
            'Bandwidth optimization'
          ]
        },
        ar: {
          overview: 'تصميم ونشر بنية تحتية للشبكة قوية وآمنة وعالية الأداء لدعم نمو عملك.',
          approach: 'خدماتنا',
          steps: [
            { title: 'الهندسة المعمارية', desc: 'تصميم البنية التحتية للشبكة' },
            { title: 'الأمن', desc: 'جدران الحماية و VPN والحماية من DDoS' },
            { title: 'النشر', desc: 'التثبيت والتكوين' },
            { title: 'الصيانة', desc: 'الدعم على مدار الساعة طوال أيام الأسبوع والمراقبة' }
          ],
          benefits: [
            'توافر 99.99٪',
            'أمان متعدد الطبقات',
            'SD-WAN للمواقع البعيدة',
            'تحسين عرض النطاق الترددي'
          ]
        }
      }
    }
  };

  const copy = COPY[language] || COPY.fr;
  const service = serviceDetails[slug];
  const steps = useReveal();
  const benefits = useReveal();

  // Une adresse inconnue renvoyait un `<div>Service not found</div>` nu, sans
  // en-tete ni pied de page ni moyen de repartir. On sert une vraie page.
  if (!service) {
    return (
      <main data-testid="tchad-service-missing" className={`${SECTION} bg-white pt-40`}>
        <div className={SHELL}>
          <div className={RULE} />
          <h1 className={`${H1} mt-6 text-slate-950`}>{copy.missingTitle}</h1>
          <p className={`mt-6 max-w-xl ${LEAD}`}>{copy.missingLead}</p>
          <Link to="/tchad/services" className={`${BTN_ON_LIGHT} mt-10`}>
            <ArrowLeft className="h-4 w-4 rtl-flip" />
            {copy.back}
          </Link>
        </div>
      </main>
    );
  }

  const serviceData = t.services[service.key];
  const details = service.details[language] || service.details.fr;

  return (
    <main data-testid="tchad-service-detail-page">
      <SEO
        title={`${serviceData.name} — Prenim Tchad`}
        description={serviceData.description}
        lang={language}
      />

      <section className="relative overflow-hidden bg-slate-950 pb-20 pt-40 text-white lg:pb-28 lg:pt-48">
        <div className="absolute inset-0 bg-tchad-weave" />
        <div className={`relative ${SHELL}`}>
          <Link
            to="/tchad/services"
            data-testid="tchad-service-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-amber-400 transition-colors hover:text-amber-300"
          >
            <ArrowLeft className="h-4 w-4 rtl-flip" />
            {copy.back}
          </Link>

          <div className="mt-8 max-w-3xl">
            <span className={EYEBROW_DARK}>{t.services.title}</span>
            <div className={`${RULE} mt-3`} />
            <h1 className={`${H1} mt-6`}>{serviceData.name}</h1>
            <p className={`mt-6 max-w-2xl ${LEAD_DARK}`}>{serviceData.description}</p>
          </div>
        </div>
      </section>

      {/* Presentation : colonne de lecture etroite plutot que pleine largeur.
          Une ligne de texte courant sur 1600 px est illisible. */}
      <section className={`${SECTION} bg-white`}>
        <div className={SHELL}>
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className={RULE} />
              <h2 className={`${H2} mt-5 text-slate-950`}>{details.approach}</h2>
            </div>
            <div className="lg:col-span-8">
              <p className="text-lg leading-relaxed text-slate-700">{details.overview}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Etapes : numerotees et soudees par des filets, pour se lire comme une
          demarche suivie et non comme quatre cartes independantes. */}
      <section className={`${SECTION} bg-slate-50`}>
        <div className={SHELL}>
          <div
            ref={steps.ref}
            className={`${steps.className} grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-4`}
          >
            {details.steps.map((step, index) => (
              <div
                key={index}
                data-testid={`tchad-step-${index}`}
                className="flex flex-col bg-white p-8"
              >
                <span className="text-sm font-bold tabular-nums text-amber-600">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-4 text-lg font-semibold tracking-tight text-slate-950">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${SECTION} bg-white`}>
        <div className={SHELL}>
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className={RULE} />
              <h2 className={`${H2} mt-5 text-slate-950`}>{copy.benefits}</h2>
            </div>
            <div className="lg:col-span-8">
              <ul ref={benefits.ref} className={`${benefits.className} grid gap-4 sm:grid-cols-2`}>
                {details.benefits.map((benefit, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 rounded-lg border border-slate-200 p-5"
                  >
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <span className="text-sm leading-relaxed text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className={`${SECTION} bg-slate-50`}>
        <div className={SHELL}>
          <div className="grid items-center gap-10 rounded-lg border border-slate-200 bg-white p-10 lg:grid-cols-12 lg:p-16">
            <div className="lg:col-span-7">
              <div className={RULE} />
              <h2 className={`${H2} mt-5 text-slate-950`}>{copy.ctaTitle}</h2>
              <p className={`mt-5 max-w-xl ${LEAD}`}>{copy.ctaLead}</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:col-span-5 lg:justify-end">
              <Link to="/tchad/contact" data-testid="tchad-detail-cta" className={BTN_PRIMARY}>
                {copy.cta}
                <ArrowRight className="h-4 w-4 rtl-flip" />
              </Link>
              <Link to="/tchad/services" className={BTN_ON_LIGHT}>
                {t.services.title}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
